function escapeWishlistHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function formatWishlistMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
  if (!match) return format;

  let formattedValue = value;
  if (match[1] === 'amount_no_decimals') {
    formattedValue = Math.round(cents / 100).toString();
  } else if (match[1] === 'amount_with_comma_separator') {
    formattedValue = value.replace('.', ',');
  } else if (match[1] === 'amount_no_decimals_with_comma_separator') {
    formattedValue = Math.round(cents / 100).toString().replace('.', ',');
  }

  return format.replace(match[0], formattedValue);
}

if (!customElements.get('wishlist-drawer')) {
  customElements.define(
    'wishlist-drawer',
    class WishlistDrawer extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
        this.querySelector('#WishlistDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.querySelector('#WishlistDrawer-Items').addEventListener('click', this.onItemsClick.bind(this));
        this.setHeaderIconAccessibility();
        this.bindExternalTriggers();
      }

      setHeaderIconAccessibility() {
        const trigger = document.querySelector('#wishlist-icon-bubble');
        if (!trigger) return;

        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
        trigger.addEventListener('keydown', (event) => {
          if (event.code && event.code.toUpperCase() === 'SPACE') {
            event.preventDefault();
            this.open(trigger);
          }
        });
      }

      // A "Wishlist" nav link (e.g. in the footer menu) opens the drawer
      // instead of navigating, since there's no dedicated wishlist page.
      // Matches either a URL containing "wishlist-drawer" (the documented
      // convention) or link text of exactly "Wishlist", so it keeps working
      // even if the merchant just typed "Wishlist" as the link's URL/title
      // in Shopify Admin rather than the "#wishlist-drawer" fragment.
      bindExternalTriggers() {
        const links = document.querySelectorAll(
          '.footer-block__details-content a, .list-menu__item--link'
        );
        links.forEach((link) => {
          const isDrawerUrl = /wishlist-drawer/i.test(link.getAttribute('href') || '');
          const isWishlistText = link.textContent.trim().toLowerCase() === 'wishlist';
          if (!isDrawerUrl && !isWishlistText) return;

          link.setAttribute('aria-haspopup', 'dialog');
          link.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(link);
          });
        });
      }

      open(triggeredBy) {
        if (this.classList.contains('active')) return;
        this.openedBy = triggeredBy;

        setTimeout(() => {
          this.classList.add('animate', 'active');
        });

        document.body.classList.add('overflow-hidden');
        this.renderItems();

        this.addEventListener(
          'transitionend',
          () => {
            const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
            if (typeof trapFocus === 'function') trapFocus(document.getElementById('WishlistDrawer'), focusElement);
          },
          { once: true },
        );
      }

      close() {
        this.classList.remove('active');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
        document.body.classList.remove('overflow-hidden');
      }

      onItemsClick(event) {
        const removeButton = event.target.closest('[data-wishlist-remove]');
        if (!removeButton) return;
        toggleWishlistProduct(removeButton.dataset.wishlistRemove);
        this.renderItems().then(() => {
          // Removing the clicked button's own DOM node drops focus outside the
          // drawer, which breaks focus-trapping and Escape-to-close. Bring focus
          // back to a stable element that survives the re-render.
          const closeButton = this.querySelector('.drawer__close');
          if (closeButton) closeButton.focus();
        });
      }

      async renderItems() {
        const listEl = this.querySelector('#WishlistDrawer-Items');
        const emptyEl = this.querySelector('#WishlistDrawer-Empty');
        const loadingEl = this.querySelector('#WishlistDrawer-Loading');
        const moneyFormat = this.dataset.moneyFormat || '{{amount}}';

        const ids = getWishlist();
        const handles = getWishlistHandles();

        listEl.innerHTML = '';
        emptyEl.hidden = true;

        if (ids.length === 0) {
          loadingEl.hidden = true;
          emptyEl.hidden = false;
          return;
        }

        loadingEl.hidden = false;

        const products = await Promise.all(
          ids.map((id) => {
            const handle = handles[id];
            if (!handle) return Promise.resolve(null);
            return fetch(`/products/${handle}.js`)
              .then((response) => (response.ok ? response.json() : null))
              .catch(() => null);
          }),
        );

        loadingEl.hidden = true;
        const validProducts = products.filter(Boolean);

        if (validProducts.length === 0) {
          emptyEl.hidden = false;
          return;
        }

        validProducts.forEach((product) => {
          const li = document.createElement('li');
          li.className = 'wishlist-drawer__item';

          const image = product.featured_image || (product.images && product.images[0]) || '';
          const title = product.title || '';
          const price = formatWishlistMoney(product.price, moneyFormat);

          const media = document.createElement('a');
          media.href = product.url;
          media.className = 'wishlist-drawer__item-media';
          if (image) {
            const img = document.createElement('img');
            img.src = image;
            img.alt = title;
            img.width = 80;
            img.height = 80;
            img.loading = 'lazy';
            media.appendChild(img);
          }

          const details = document.createElement('div');
          details.className = 'wishlist-drawer__item-details';
          details.innerHTML = `
            <a href="${product.url}" class="wishlist-drawer__item-title">${escapeWishlistHtml(title)}</a>
            <span class="wishlist-drawer__item-price">${escapeWishlistHtml(price)}</span>
          `;

          const removeButton = document.createElement('button');
          removeButton.type = 'button';
          removeButton.className = 'wishlist-drawer__item-remove';
          removeButton.dataset.wishlistRemove = product.id;
          removeButton.setAttribute('aria-label', `Remove ${title} from wishlist`);
          removeButton.innerHTML = '<span class="svg-wrapper">&times;</span>';

          li.appendChild(media);
          li.appendChild(details);
          li.appendChild(removeButton);
          listEl.appendChild(li);
        });
      }
    },
  );
}
